/* bm_peakExplorer_main.js – Orchestrator */
(function () {
  window.BM = window.BM || {};
  const BM_PEAK_EXPLORER_MAIN_VERSION = 9;
  if (window.BM.__peakExplorerMainVersion === BM_PEAK_EXPLORER_MAIN_VERSION) return;
  window.BM.__peakExplorerMainVersion = BM_PEAK_EXPLORER_MAIN_VERSION;
  window.BM.__peakExplorerMainLoaded = true;

  const DEFAULT_CONFIG = Object.freeze({
    token: '',
    initialCenter: [10.99, 47.421],
    printSizeCm: 15,
    printSizeLabel: '15 cm',
    scaleMin: 20000,
    scaleMax: 200000,
    scaleStep: 5000,
    initialScale: 100000,
    initialEdgeKm: 10,
    rotationDeg: 0,
    styleDefault: '',
    language: 'de',
    countries: 'de,at,ch,it,fr'
  });

  const DEFAULT_STYLES = Object.freeze([
    { label: 'Outdoor', uri: 'mapbox://styles/mapbox/outdoors-v12' },
    { label: 'Weiß', uri: 'mapbox://styles/ottmar/cmg1c5suk001k01s918dtf323' },
    { label: 'Grau Hell', uri: 'mapbox://styles/ottmar/cmg1d8bx4005601s7ar6qe908' },
    { label: 'Grau Dunkel', uri: 'mapbox://styles/ottmar/cmg1dbfwe001l01sccv8ueqg5' },
    { label: 'Holz Hell', uri: 'mapbox://styles/ottmar/cmg1dggqh005701s7fdfk9y02' }
  ]);

  const ERROR_MESSAGES = Object.freeze({
    init: 'Peak Explorer konnte nicht initialisiert werden.',
    mapLibrary: 'Kartenbibliothek konnte nicht geladen werden.',
    mapCreate: 'Kartenansicht konnte nicht initialisiert werden.',
    mapLoad: 'Kartenansicht konnte nicht geladen werden.'
  });

  const MAPBOX_GL_SCRIPT_URL = 'https://api.mapbox.com/mapbox-gl-js/v3.25.0/mapbox-gl.js';
  const MAPBOX_GL_LOAD_TIMEOUT_MS = 15000;
  const GEOLOCATION_TIMEOUT_MS = 12000;
  const GEOLOCATION_MAX_AGE_MS = 300000;
  const INTERACTION_STATUS_DURATION_MS = 5000;
  const BM_3D_PREVIEW_API_READY_EVENT = 'bm:3dPreview:api-ready';
  const BM_HIDDEN_SNOWLINE_M = 2800;
  const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
  let mapboxGlLoadPromise = null;

  function initPeakExplorerSections(scope = document) {
    const roots = getRoots(scope);
    if (!roots.length) return;

    const activeRoots = getActiveRoots(roots, true);
    if (isMapboxGlReady()) {
      activeRoots.forEach(initSinglePeakExplorerSection);
      return;
    }

    activeRoots.forEach(root => setMapLibraryPending(root, true));
    ensureMapboxGl().then(isReady => {
      activeRoots.filter(root => root.isConnected).forEach(root => {
        setMapLibraryPending(root, false);
        if (isReady) {
          initSinglePeakExplorerSection(root);
        } else {
          showMapError(root, ERROR_MESSAGES.mapLibrary);
        }
      });
    });
  }

  function cleanupPeakExplorerSections(scope = document) {
    const roots = getActiveRoots(getRoots(scope), false);
    roots.forEach(root => root.__bmCleanup?.());
  }

  function getRoots(scope) {
    if (!scope) return [];

    if (scope instanceof Element && scope.matches('.bm-custom-peak')) {
      return [scope];
    }

    if (typeof scope.querySelectorAll === 'function') {
      return Array.from(scope.querySelectorAll('.bm-custom-peak'));
    }

    return [];
  }

  function isMapboxGlReady() {
    return typeof window.mapboxgl?.Map === 'function'
      && typeof window.mapboxgl?.supported === 'function';
  }

  function ensureMapboxGl() {
    if (isMapboxGlReady()) return Promise.resolve(true);
    if (mapboxGlLoadPromise) return mapboxGlLoadPromise;

    mapboxGlLoadPromise = new Promise(resolve => {
      const script = document.createElement('script');
      let isSettled = false;
      let timeoutId = null;

      const finish = isReady => {
        if (isSettled) return;
        isSettled = true;
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);

        if (!isReady) script.remove();
        resolve(isReady);
      };

      const onLoad = () => finish(isMapboxGlReady());
      const onError = () => finish(false);

      script.src = MAPBOX_GL_SCRIPT_URL;
      script.async = true;
      script.dataset.bmPeakExplorerMapboxGl = '';
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      timeoutId = window.setTimeout(
        () => finish(false),
        MAPBOX_GL_LOAD_TIMEOUT_MS
      );

      try {
        document.head.appendChild(script);
      } catch {
        finish(false);
      }
    }).then(isReady => {
      if (!isReady) mapboxGlLoadPromise = null;
      return isReady;
    });

    return mapboxGlLoadPromise;
  }

  function setMapLibraryPending(root, isPending) {
    const mapElement = root?.querySelector?.('[data-role="map"]');
    if (mapElement) mapElement.setAttribute('aria-busy', String(isPending));
    if (isPending) hideMapError(root);
  }

  function getActiveRoots(roots, hideDuplicates) {
    if (!Array.isArray(roots) || !roots.length) return [];
    if (roots.length === 1) return roots;

    const sortedRoots = roots.slice().sort((a, b) => scoreRoot(b) - scoreRoot(a));
    const primaryRoot = sortedRoots[0];

    if (hideDuplicates) {
      sortedRoots.slice(1).forEach(root => {
        if (!root || root.__bmDuplicateHidden) return;
        root.__bmDuplicateHidden = true;
        root.hidden = true;
        root.setAttribute('aria-hidden', 'true');
      });
    }

    return primaryRoot ? [primaryRoot] : [];
  }

  function scoreRoot(root) {
    if (!root) return -1;

    let score = 0;
    if (!root.hidden && (root.offsetWidth || root.offsetHeight || root.getClientRects?.().length)) {
      score += 1000;
    }

    const rect = root.getBoundingClientRect?.();
    if (rect) {
      score += Math.max(0, rect.width) + Math.max(0, rect.height);
    }

    if (root.closest?.('main, [role="main"], .main-content, #MainContent')) {
      score += 100;
    }

    return score;
  }

  function initSinglePeakExplorerSection(root) {
    if (!root || root.__bmInited) return;
    root.__bmInited = true;

    const sectionId = root.dataset.sectionId || '';
    const U = window.BM?.util;
    const mapEl = root.querySelector('.bm-custom-map');
    const printSizeEl = root.querySelector('[data-role="print-size"]');
    const coordsEl = root.querySelector('[data-role="coords"]');
    const worldWidthEl =
      root.querySelector('[data-role="world-width"]') ||
      root.querySelector('[data-role="world-length"]');
    const worldLengthEl = root.querySelector('[data-role="world-length"]');
    const scaleSlider = root.querySelector('[data-role="scale-slider"]');
    const styleSelect = root.querySelector('[data-role="style-select"]');
    const rotationSlider = root.querySelector('[data-role="rotation-slider"]');
    const rotationLabelEl = root.querySelector('[data-role="rotation-label"]');
    const mapActionsEl = root.querySelector('[data-role="map-actions"]');
    const mapHelpEl = root.querySelector('[data-role="map-help"]');
    const selectionLockButton = root.querySelector('[data-role="toggle-selection-lock"]');
    const shareLinkButton = root.querySelector('[data-role="share-link"]');
    let locationButton = null;
    const previewToggleButton = root.querySelector('[data-role="toggle-3d-preview"]');
    const interactionStatusEl = root.querySelector('[data-role="interaction-status"]');
    const snowControlEnabled = root.dataset.snowControlEnabled === 'true';
    const addonBridgeAvailable =
      typeof window.BM?.bm_peakExplorer_readAddonState === 'function';
    let addonState = addonBridgeAvailable
      ? window.BM.bm_peakExplorer_readAddonState(root)
      : Object.freeze({ available: true, gpxBooked: false, snowBooked: false });
    const bmStyles = getAvailableStyles();
    const cfg = readSectionConfig(root, sectionId, U);
    const sharedState = typeof window.BM?.bm_peakExplorer_readShareState === 'function'
      ? window.BM.bm_peakExplorer_readShareState()
      : null;

    let map = null;
    let marker = null;
    let rect = null;
    let ui = null;
    let scaleSliderWasWired = false;
    let rotationSliderWasWired = false;
    let focusResetTimeoutId = null;
    let hasLoadedStyle = false;
    let isStyleTransitionPending = false;
    let state = null;
    let orderProps = null;
    let interactionStatusTimeoutId = null;
    let mobileRevealFrameId = null;
    let geolocationRequestId = 0;
    let isDisposed = false;
    let selectionLock = null;
    let locationControl = null;
    let fullscreenControl = null;
    let infoControl = null;
    let gpxUpload = null;

    const cleanupFns = [];
    const ids = createLayerIds(sectionId);
    const rectColor = getRectColor(root);
    const desiredStyle = cfg?.styleDefault || bmStyles[0]?.uri || DEFAULT_STYLES[0].uri;

    function failInit(message) {
      showMapError(root, message);
      root.__bmInited = false;
      root.__bmCleanup = null;
    }

    if (!U) {
      failInit(ERROR_MESSAGES.init);
      return;
    }

    if (!isValidConfig(cfg)) {
      failInit(ERROR_MESSAGES.init);
      return;
    }

    state = createState(cfg, U);
    applySharedState(state, sharedState, U);

    if (!mapEl) {
      failInit(ERROR_MESSAGES.mapCreate);
      return;
    }

    if (typeof mapboxgl === 'undefined') {
      failInit(ERROR_MESSAGES.mapLibrary);
      return;
    }

    if (typeof window.BM?.initMap !== 'function') {
      failInit(ERROR_MESSAGES.mapCreate);
      return;
    }

    if (!isValidPublicMapboxToken(cfg.token)) {
      failInit(ERROR_MESSAGES.mapLoad);
      return;
    }

    mapboxgl.accessToken = cfg.token;

    syncReadOnlyUi();
    buildStyleSelect();

    const mapKit = window.BM.initMap(mapEl, state.center, rectColor, desiredStyle);
    if (!mapKit?.map || !mapKit?.marker) {
      failInit(ERROR_MESSAGES.mapLoad);
      return;
    }

    map = mapKit.map;
    marker = mapKit.marker;
    if (typeof mapKit.cleanup === 'function') cleanupFns.push(mapKit.cleanup);

    orderProps = typeof window.BM?.setupOrderProps === 'function'
      ? window.BM.setupOrderProps(state, root)
      : null;
    if (typeof orderProps?.destroy === 'function') cleanupFns.push(orderProps.destroy);

    hideMapError(root);

    if (typeof window.BM?.attachSearch === 'function') {
      const searchCleanup = window.BM.attachSearch(root, map, state, cfg.token, (lngLat, resultInfo) => {
        if (selectionLock?.isLocked?.()) {
          announceInteraction('Die Koordinaten sind gesperrt. Bitte zuerst entsperren.', { isError: true });
          return;
        }
        marker.setLngLat(lngLat);
        applyCenter(lngLat.lng, lngLat.lat, true, true);
        announceInteraction(
          resultInfo?.label ? `${resultInfo.label} ausgewählt.` : 'Suchergebnis ausgewählt.'
        );
        revealMapOnMobile();
      });

      if (typeof searchCleanup === 'function') {
        cleanupFns.push(() => searchCleanup());
      }
    }

    bindMapInteractions();
    initUi();
    initVariantBridge();
    wireRotationSlider();
    wireCompassReset();
    wireMapActions();

    const handleTrackFocus = event => {
      const longitude = Number(event.detail?.center?.[0]);
      const latitude = Number(event.detail?.center?.[1]);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return;
      if (selectionLock?.isLocked?.()) {
        announceInteraction('Die Koordinaten sind gesperrt. Bitte zuerst entsperren.', {
          isError: true
        });
        return;
      }

      marker.setLngLat({ lng: longitude, lat: latitude });
      applyCenter(longitude, latitude, true, true);
      announceInteraction('Auswahl wurde auf den GPS-Track zentriert.');
    };
    root.addEventListener('bm:peakExplorer:focus-track', handleTrackFocus);
    cleanupFns.push(() => {
      root.removeEventListener('bm:peakExplorer:focus-track', handleTrackFocus);
    });

    map.on('error', handleMapError);
    cleanupFns.push(() => map.off('error', handleMapError));

    map.on('style.load', handleStyleLoad);
    cleanupFns.push(() => map.off('style.load', handleStyleLoad));

    if (map.isStyleLoaded?.()) handleStyleLoad();
    root.__bmCleanup = cleanup;

    function syncReadOnlyUi() {
      updatePrintUI();
      updateCoordUI();
      updateWorldSizeUI();
      updateRotationUI();
    }

    function updatePrintUI() {
      if (!printSizeEl) return;
      printSizeEl.textContent = state.printSizeLabel || `${U.formatCmValue(state.printSizeCm)} cm`;
    }

    function updateCoordUI() {
      if (!coordsEl) return;
      coordsEl.textContent = `${state.center[1].toFixed(5)}, ${state.center[0].toFixed(5)}`;
    }

    function updateWorldSizeUI() {
      if (worldWidthEl) worldWidthEl.textContent = U.formatKmValue(state.worldWidthKm);
      if (worldLengthEl) worldLengthEl.textContent = U.formatKmValue(state.worldHeightKm);
    }

    function updateRotationUI() {
      if (rotationLabelEl) {
        rotationLabelEl.textContent = `${Math.round(state.rotationDeg)}°`;
      }
      if (rotationSlider) {
        rotationSlider.value = String(Math.round(state.rotationDeg));
        rotationSlider.setAttribute(
          'aria-valuetext',
          `${Math.round(state.rotationDeg)} Grad im Uhrzeigersinn`
        );
      }
    }

    function syncScaleUi() {
      if (!ui) return;

      const labelDenom = Math.round(state.scaleDenom / 100) * 100;
      let sliderDenom = U.roundTo(state.scaleDenom, state.scaleStep);
      sliderDenom = U.clamp(sliderDenom, state.scaleMin, state.scaleMax);

      ui.setScaleLabel?.(labelDenom);
      ui.setScaleSlider?.(sliderDenom, state.scaleMin, state.scaleMax);
    }

    function writeOrderProps(commit = true) {
      if (!orderProps) return;
      if (commit) {
        orderProps.write?.();
      } else {
        orderProps.writeThrottled?.();
      }
    }

    function getRectDimensions() {
      return {
        widthKm: state.worldWidthKm,
        heightKm: state.worldHeightKm,
        rotationDeg: state.rotationDeg
      };
    }

    function bm_peakExplorer_publishSelection(selection) {
      if (!selection) return;

      const detail = Object.freeze({
        sectionId,
        center: Object.freeze(selection.center.slice()),
        bounds: Object.freeze(selection.bounds.slice()),
        widthKm: Number(selection.widthKm),
        heightKm: Number(selection.heightKm),
        printWidthCm: Number(state.printLongEdgeCm),
        printHeightCm: Number(state.printShortEdgeCm),
        rotationDeg: Number(selection.rotationDeg || 0),
        polygon: Object.freeze(
          selection.polygon.map((coordinate) => Object.freeze(coordinate.slice()))
        )
      });

      root.bm_peakExplorer_selection = detail;
      root.dispatchEvent(
        new CustomEvent('bm:peakExplorer:selection-change', {
          bubbles: true,
          detail
        })
      );
    }

    function recomputeDerivedWorldGeometry() {
      state.worldLongEdgeKm = state.worldShortEdgeKm * state.printAspectRatio;
      state.worldWidthKm = state.worldLongEdgeKm;
      state.worldHeightKm = state.worldShortEdgeKm;
      updateWorldSizeUI();
    }

    function applyCenter(lng, lat, fit = false, commit = false) {
      state.center = [lng, lat];
      rect?.update(state.center, getRectDimensions(), fit);
      updateCoordUI();
      writeOrderProps(commit);
    }

    function setShortEdgeKm(nextEdgeKm) {
      const edgeKm = Number(nextEdgeKm);
      if (!Number.isFinite(edgeKm) || edgeKm <= 0) return false;
      if (U.nearlyEqual(state.worldShortEdgeKm, edgeKm, 1e-6)) return false;

      state.worldShortEdgeKm = edgeKm;
      recomputeDerivedWorldGeometry();

      let exactDenom = U.scaleFromEdge(state.worldShortEdgeKm, state.printShortEdgeCm);
      if (!Number.isFinite(exactDenom) || exactDenom <= 0) {
        exactDenom = state.scaleDenom;
      }

      state.scaleDenom = exactDenom;
      return true;
    }

    function setScaleDenom(nextDenom) {
      let denom = U.roundTo(Number(nextDenom), state.scaleStep);
      denom = U.clamp(denom, state.scaleMin, state.scaleMax);

      if (!Number.isFinite(denom) || denom <= 0) return false;
      if (U.nearlyEqual(state.scaleDenom, denom, 1e-9)) return false;

      state.scaleDenom = denom;
      state.worldShortEdgeKm = U.edgeKmFromScale(state.printShortEdgeCm, state.scaleDenom);
      recomputeDerivedWorldGeometry();
      return true;
    }

    function setRotationDeg(nextRotation) {
      let rotation = Number(nextRotation);
      if (!Number.isFinite(rotation)) return false;

      rotation = U.clamp(Math.round(rotation), 0, 180);
      if (U.nearlyEqual(state.rotationDeg, rotation, 1e-9)) return false;

      state.rotationDeg = rotation;
      updateRotationUI();
      return true;
    }

    function setPrintSize(nextSize) {
      const sizeInfo = U.sizeInfoFromUnknown(nextSize);
      if (!sizeInfo) return false;

      const sameCm = U.nearlyEqual(state.printSizeCm, sizeInfo.printSizeCm, 1e-6);
      const sameLabel = state.printSizeLabel === sizeInfo.displayLabel;
      if (sameCm && sameLabel) return false;

      state.printSizeCm = sizeInfo.printSizeCm;
      state.printSizeLabel = sizeInfo.displayLabel;
      state.printShortEdgeCm = sizeInfo.shortestEdgeCm;
      state.printLongEdgeCm = sizeInfo.longestEdgeCm;
      state.printAspectRatio = sizeInfo.aspectRatio || 1;
      state.scaleDenom = U.roundTo(
        U.scaleFromEdge(state.worldShortEdgeKm, state.printShortEdgeCm),
        state.scaleStep
      );

      recomputeDerivedWorldGeometry();
      updatePrintUI();
      return true;
    }

    function getTopLabelLayerId() {
      const layers = map?.getStyle?.()?.layers || [];
      const candidates = ['poi-label', 'transit-label', 'settlement', 'road-label', 'waterway-label'];

      for (const candidate of candidates) {
        const matchingLayer = layers.find(layer => layer.id.includes(candidate));
        if (matchingLayer) return matchingLayer.id;
      }

      return null;
    }

    function buildStyleSelect() {
      if (!styleSelect) return;

      styleSelect.replaceChildren();

      bmStyles.forEach(styleEntry => {
        const optionEl = document.createElement('option');
        optionEl.value = styleEntry.uri;
        optionEl.textContent = styleEntry.label;
        styleSelect.appendChild(optionEl);
      });

      styleSelect.value = desiredStyle;

      const onStyleChange = () => {
        const nextStyle = styleSelect.value;
        if (!nextStyle || !map) return;

        try {
          isStyleTransitionPending = true;
          map.setStyle(nextStyle);
        } catch {
          isStyleTransitionPending = false;
          showMapError(root, ERROR_MESSAGES.mapLoad);
          updateStyleSelectValue();
        }
      };

      styleSelect.addEventListener('change', onStyleChange);
      cleanupFns.push(() => styleSelect.removeEventListener('change', onStyleChange));
    }

    function updateStyleSelectValue() {
      if (!styleSelect || !map) return;

      const sprite = map.getStyle?.()?.sprite || '';
      const matchingStyle = bmStyles.find(style => sprite.includes(extractStyleName(style.uri)));

      if (matchingStyle) {
        styleSelect.value = matchingStyle.uri;
      }
    }

    function bindMapInteractions() {
      if (!map || !marker) return;

      const throttledMarkerDrag = U.rafThrottle(() => {
        if (selectionLock?.isLocked?.()) return;
        const markerLngLat = marker.getLngLat();
        applyCenter(markerLngLat.lng, markerLngLat.lat, false, false);
      });

      const onMarkerDragEnd = () => {
        if (selectionLock?.isLocked?.()) return;
        const markerLngLat = marker.getLngLat();
        applyCenter(markerLngLat.lng, markerLngLat.lat, true, true);
        announceInteraction('Auswahl aktualisiert.');
      };

      const onMapClick = event => {
        if (selectionLock?.isLocked?.()) return;
        marker.setLngLat(event.lngLat);
        applyCenter(event.lngLat.lng, event.lngLat.lat, false, true);
        announceInteraction('Auswahl aktualisiert.');
      };

      marker.on('drag', throttledMarkerDrag);
      marker.on('dragend', onMarkerDragEnd);
      map.on('click', onMapClick);

      cleanupFns.push(() => marker.off('drag', throttledMarkerDrag));
      cleanupFns.push(() => marker.off('dragend', onMarkerDragEnd));
      cleanupFns.push(() => map.off('click', onMapClick));
      cleanupFns.push(() => throttledMarkerDrag.cancel?.());
    }

    function wireCompassReset() {
      if (!mapEl) return;

      const attachHandler = () => {
        const compassButton = mapEl.querySelector('.mapboxgl-ctrl-compass');
        if (!compassButton || compassButton.__bmResetBound) return;

        const onCompassClick = (event) => {
          event.preventDefault();
          event.stopPropagation();
          focusSelectionNorth();
        };

        compassButton.__bmResetBound = true;
        compassButton.addEventListener('click', onCompassClick, true);
        cleanupFns.push(() => {
          compassButton.removeEventListener('click', onCompassClick, true);
          compassButton.__bmResetBound = false;
        });
      };

      const compassFrameId = window.requestAnimationFrame(attachHandler);
      cleanupFns.push(() => window.cancelAnimationFrame(compassFrameId));
    }

    function focusSelectionNorth() {
      if (!map) return;

      if (focusResetTimeoutId) {
        clearTimeout(focusResetTimeoutId);
        focusResetTimeoutId = null;
      }

      const duration = U.prefersReducedMotion?.() ? 0 : 350;
      map.easeTo({
        center: state.center,
        bearing: 0,
        pitch: 0,
        duration,
        essential: false
      });

      if (duration === 0) {
        rect?.update(state.center, getRectDimensions(), true);
        return;
      }

      focusResetTimeoutId = window.setTimeout(() => {
        focusResetTimeoutId = null;
        rect?.update(state.center, getRectDimensions(), true);
      }, duration + 10);
    }

    function announceInteraction(message, options = {}) {
      if (!interactionStatusEl || typeof message !== 'string' || !message.trim()) return;

      if (interactionStatusTimeoutId) {
        window.clearTimeout(interactionStatusTimeoutId);
        interactionStatusTimeoutId = null;
      }

      interactionStatusEl.textContent = message.trim();
      interactionStatusEl.classList.toggle('bm-custom-status--error', options.isError === true);
      interactionStatusEl.hidden = false;

      if (options.persistent === true) return;

      interactionStatusTimeoutId = window.setTimeout(() => {
        interactionStatusTimeoutId = null;
        interactionStatusEl.hidden = true;
        interactionStatusEl.textContent = '';
        interactionStatusEl.classList.remove('bm-custom-status--error');
      }, INTERACTION_STATUS_DURATION_MS);
    }

    function clearSearchQuery() {
      const searchElement = root.querySelector('mapbox-search-box');
      if (!searchElement || !window.customElements?.get?.('mapbox-search-box')) return;
      searchElement.value = '';
    }

    function revealMapOnMobile() {
      if (
        !mapEl
        || typeof window.matchMedia !== 'function'
        || !window.matchMedia(MOBILE_MEDIA_QUERY).matches
      ) {
        return;
      }

      if (mobileRevealFrameId) {
        window.cancelAnimationFrame(mobileRevealFrameId);
      }

      mobileRevealFrameId = window.requestAnimationFrame(() => {
        mobileRevealFrameId = null;
        if (isDisposed || !mapEl.isConnected) return;

        const bounds = mapEl.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const isFullyVisible = bounds.top >= 0 && bounds.bottom <= viewportHeight;
        if (isFullyVisible) return;

        mapEl.scrollIntoView({
          behavior: U.prefersReducedMotion?.() ? 'auto' : 'smooth',
          block: 'center'
        });
      });
    }

    function getGeolocationErrorMessage(error) {
      if (error?.code === 1) {
        return 'Standortfreigabe wurde nicht erteilt.';
      }
      if (error?.code === 3) {
        return 'Die Standortabfrage hat zu lange gedauert. Bitte erneut versuchen.';
      }
      return 'Der aktuelle Standort konnte nicht ermittelt werden.';
    }

    function setLocationPending(isPending) {
      if (!locationButton) return;
      if (typeof locationControl?.setPending === 'function') {
        locationControl.setPending(isPending);
        return;
      }
      locationButton.disabled = isPending;
      locationButton.setAttribute('aria-busy', String(isPending));
    }

    function requestCurrentLocation() {
      if (!navigator.geolocation || !locationButton || isDisposed) return;
      if (selectionLock?.isLocked?.()) {
        announceInteraction('Die Koordinaten sind gesperrt. Bitte zuerst entsperren.', { isError: true });
        return;
      }

      const requestId = ++geolocationRequestId;
      setLocationPending(true);
      announceInteraction('Aktueller Standort wird ermittelt…', { persistent: true });

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (isDisposed || requestId !== geolocationRequestId) return;
          setLocationPending(false);
          if (selectionLock?.isLocked?.()) {
            announceInteraction('Die Koordinaten wurden zwischenzeitlich gesperrt.', { isError: true });
            return;
          }

          const lng = Number(position?.coords?.longitude);
          const lat = Number(position?.coords?.latitude);
          if (
            !Number.isFinite(lng)
            || lng < -180
            || lng > 180
            || !Number.isFinite(lat)
            || lat < -85.051129
            || lat > 85.051129
          ) {
            announceInteraction('Der aktuelle Standort konnte nicht übernommen werden.', {
              isError: true
            });
            return;
          }

          clearSearchQuery();
          marker.setLngLat({ lng, lat });
          applyCenter(lng, lat, true, true);
          announceInteraction('Aktueller Standort ausgewählt.');
        },
        (error) => {
          if (isDisposed || requestId !== geolocationRequestId) return;
          setLocationPending(false);
          announceInteraction(getGeolocationErrorMessage(error), { isError: true });
        },
        {
          enableHighAccuracy: true,
          timeout: GEOLOCATION_TIMEOUT_MS,
          maximumAge: GEOLOCATION_MAX_AGE_MS
        }
      );
    }

    function wireMapActions() {
      if (!map) return;
      if (mapActionsEl) mapActionsEl.hidden = false;

      if (
        navigator.geolocation &&
        typeof window.BM?.bm_peakExplorer_attachLocationControl === 'function'
      ) {
        locationControl = window.BM.bm_peakExplorer_attachLocationControl({
          map,
          onActivate: requestCurrentLocation
        });
        locationButton = locationControl?.button || null;
        if (typeof locationControl?.destroy === 'function') {
          const attachedLocationControl = locationControl;
          cleanupFns.push(() => attachedLocationControl.destroy());
        }
        if (!locationControl) {
          announceInteraction('Die Standortfunktion konnte nicht geladen werden.', {
            isError: true
          });
        }
      } else if (
        navigator.geolocation &&
        typeof window.BM?.bm_peakExplorer_attachLocationControl !== 'function'
      ) {
        announceInteraction('Die Standortfunktion konnte nicht geladen werden.', {
          isError: true
        });
      }

      if (typeof window.BM?.bm_peakExplorer_attachFullscreenControl === 'function') {
        fullscreenControl = window.BM.bm_peakExplorer_attachFullscreenControl({
          map,
          onStatus: announceInteraction
        });
        if (typeof fullscreenControl?.destroy === 'function') {
          const attachedFullscreenControl = fullscreenControl;
          cleanupFns.push(() => attachedFullscreenControl.destroy());
        }
      } else {
        announceInteraction('Die Vollbildfunktion konnte nicht geladen werden.', {
          isError: true
        });
      }

      if (typeof window.BM?.bm_peakExplorer_attachInfoControl === 'function') {
        infoControl = window.BM.bm_peakExplorer_attachInfoControl({
          map,
          text: mapHelpEl?.textContent
        });
        if (typeof infoControl?.destroy === 'function') {
          const attachedInfoControl = infoControl;
          cleanupFns.push(() => attachedInfoControl.destroy());
        }
      } else {
        announceInteraction('Die Kartenhinweise konnten nicht geladen werden.', {
          isError: true
        });
      }

      if (selectionLockButton && typeof window.BM?.bm_peakExplorer_attachSelectionLock === 'function') {
        selectionLock = window.BM.bm_peakExplorer_attachSelectionLock({
          root,
          button: selectionLockButton,
          marker,
          onStatus: announceInteraction,
          onChange: (isLocked) => locationControl?.setDisabled?.(isLocked)
        });
        root.bm_peakExplorer_selectionLock = selectionLock;
        locationControl?.setDisabled?.(selectionLock?.isLocked?.());
        if (sharedState?.locked === true) {
          selectionLock?.setLocked?.(true, false);
        }
        if (typeof selectionLock?.destroy === 'function') {
          const attachedSelectionLock = selectionLock;
          cleanupFns.push(() => {
            attachedSelectionLock.destroy();
            if (root.bm_peakExplorer_selectionLock === attachedSelectionLock) {
              delete root.bm_peakExplorer_selectionLock;
            }
          });
        }
      }

      if (snowControlEnabled) {
        if (typeof window.BM?.bm_peakExplorer_attachSnowPreview === 'function') {
          const cleanupSnowPreview = window.BM.bm_peakExplorer_attachSnowPreview({
            root,
            map,
            onStatus: announceInteraction
          });
          if (typeof cleanupSnowPreview === 'function') {
            cleanupFns.push(cleanupSnowPreview);
          } else {
            announceInteraction('Die Schnee-Vorvorschau ist für diese Karte nicht verfügbar.', {
              isError: true
            });
          }
        } else {
          announceInteraction('Die Schnee-Vorvorschau konnte nicht geladen werden.', {
            isError: true
          });
        }

        if (typeof window.BM?.bm_peakExplorer_attachSnowControl === 'function') {
          const initialSnowState = Object.assign({}, sharedState || {}, {
            snowEnabled: addonState.available ? addonState.snowBooked : true
          });
          const cleanupSnowControl = window.BM.bm_peakExplorer_attachSnowControl({
            root,
            onStatus: announceInteraction,
            initialState: initialSnowState
          });
          if (typeof cleanupSnowControl === 'function') {
            cleanupFns.push(cleanupSnowControl);
          }
        } else {
          announceInteraction('Die Schnee-Steuerung konnte nicht geladen werden.', {
            isError: true
          });
        }
      } else {
        bm_peakExplorer_forceSnowDisabled();
      }

      if (shareLinkButton && typeof window.BM?.bm_peakExplorer_attachShare === 'function') {
        const cleanupShare = window.BM.bm_peakExplorer_attachShare({
          root,
          button: shareLinkButton,
          onStatus: announceInteraction,
          getState: () => {
            const snowSlider = root.querySelector('[data-role="snow-slider"]');
            const snowMode = snowControlEnabled
              ? String(root.dataset.bmSnowMode || 'off')
              : 'off';
            return {
              center: state.center.slice(),
              edgeKm: state.worldShortEdgeKm,
              scaleDenom: state.scaleDenom,
              rotationDeg: state.rotationDeg,
              snowEnabled: snowMode !== 'off',
              snowMode,
              snowlineM: snowControlEnabled
                ? Number(snowSlider?.value || BM_HIDDEN_SNOWLINE_M)
                : BM_HIDDEN_SNOWLINE_M,
              locked: Boolean(selectionLock?.isLocked?.())
            };
          }
        });
        if (typeof cleanupShare === 'function') {
          cleanupFns.push(cleanupShare);
        } else {
          announceInteraction('Die Link-Funktion konnte nicht initialisiert werden.', {
            isError: true
          });
        }
      } else if (shareLinkButton) {
        announceInteraction('Die Link-Funktion konnte nicht geladen werden.', {
          isError: true
        });
      }

      if (previewToggleButton && typeof window.BM?.bm_peakExplorer_attachPreviewToggle === 'function') {
        const cleanupPreviewToggle = window.BM.bm_peakExplorer_attachPreviewToggle({
          root,
          button: previewToggleButton,
          onStatus: announceInteraction
        });
        if (typeof cleanupPreviewToggle === 'function') {
          cleanupFns.push(cleanupPreviewToggle);
        }
      }

      if (
        root.dataset.gpxUploadEnabled === 'true' &&
        typeof window.BM?.bm_peakExplorer_attachGpxUpload === 'function'
      ) {
        gpxUpload = window.BM.bm_peakExplorer_attachGpxUpload({
          root,
          map,
          onStatus: announceInteraction,
          enabled: !addonState.available || addonState.gpxBooked
        });
        if (typeof gpxUpload?.destroy === 'function') {
          const attachedGpxUpload = gpxUpload;
          cleanupFns.push(() => attachedGpxUpload.destroy());
        }
      }

      if (addonState.available) {
        if (typeof window.BM?.bm_peakExplorer_attachAddonGate === 'function') {
          const cleanupAddonGate = window.BM.bm_peakExplorer_attachAddonGate({
            root,
            onChange: (nextState) => {
              addonState = nextState;
              gpxUpload?.setEnabled?.(nextState.gpxBooked);
            }
          });
          if (typeof cleanupAddonGate === 'function') {
            cleanupFns.push(cleanupAddonGate);
          }
        } else {
          announceInteraction('Die Zusatzleistungen konnten nicht mit der Karte verbunden werden.', {
            isError: true
          });
        }
      } else {
        root.querySelectorAll('.bm-addon-panel').forEach(panel => {
          panel.dataset.bmAddonBooked = 'true';
          panel.setAttribute('aria-disabled', 'false');
          panel.open = true;
          const content = panel.querySelector('.bm-addon-panel-content');
          const status = panel.querySelector('[data-role="addon-status"]');
          if (content) content.inert = false;
          if (status) status.textContent = 'Verfügbar';
        });
      }

      cleanupFns.push(() => {
        setLocationPending(false);
        locationButton = null;
        locationControl = null;
        fullscreenControl = null;
        infoControl = null;
        gpxUpload = null;
        if (mapActionsEl) mapActionsEl.hidden = true;
      });
    }

    function bm_peakExplorer_forceSnowDisabled() {
      const applyDisabledState = () => {
        const setSnowSettings = window.BM?.bm_3dPreview_setSnowSettings;
        if (typeof setSnowSettings !== 'function') return false;

        const wasAccepted = setSnowSettings({
          sectionId: '',
          enabled: false,
          mode: 'off',
          baseSnowlineM: BM_HIDDEN_SNOWLINE_M
        });
        if (!wasAccepted) {
          announceInteraction('Der Schnee in der 3D-Vorschau konnte nicht deaktiviert werden.', {
            isError: true
          });
        }
        return true;
      };

      if (applyDisabledState()) return;

      const onPreviewApiReady = () => {
        applyDisabledState();
      };
      document.addEventListener(BM_3D_PREVIEW_API_READY_EVENT, onPreviewApiReady, {
        once: true
      });
      cleanupFns.push(() => {
        document.removeEventListener(BM_3D_PREVIEW_API_READY_EVENT, onPreviewApiReady);
      });
    }

    function initUi() {
      if (ui || typeof window.BM?.attachUI !== 'function') return;

      ui = window.BM.attachUI(root, state.worldShortEdgeKm, (newEdgeKm, fit) => {
        if (!setShortEdgeKm(newEdgeKm)) return;

        syncScaleUi();
        rect?.update(state.center, getRectDimensions(), fit);
        writeOrderProps(true);
      });
      if (typeof ui?.destroy === 'function') cleanupFns.push(ui.destroy);

      syncScaleUi();
      wireScaleSlider();
    }

    function initVariantBridge() {
      if (typeof window.BM?.initVariantBridge !== 'function') return;

      const cleanupVariantBridge = window.BM.initVariantBridge(
        root,
        (sizeInfo, fit = false) => {
          if (!setPrintSize(sizeInfo)) return;

          syncScaleUi();
          rect?.update(state.center, getRectDimensions(), fit);
          writeOrderProps(true);
        },
        (newDenom, fit = false) => {
          if (!setScaleDenom(newDenom)) return;

          ui?.setDiameter?.(state.worldShortEdgeKm, fit);
          syncScaleUi();
          rect?.update(state.center, getRectDimensions(), fit);
          writeOrderProps(true);
        },
        () => {}
      );

      if (typeof cleanupVariantBridge === 'function') {
        cleanupFns.push(() => cleanupVariantBridge());
      }
    }

    function wireScaleSlider() {
      if (scaleSliderWasWired || !scaleSlider) return;
      scaleSliderWasWired = true;

      scaleSlider.min = String(state.scaleMin);
      scaleSlider.max = String(state.scaleMax);
      scaleSlider.step = String(state.scaleStep);
      scaleSlider.value = String(state.scaleDenom);

      const softUpdate = U.rafThrottle(() => {
        if (!setScaleDenom(scaleSlider.value)) return;

        ui?.setDiameter?.(state.worldShortEdgeKm, false);
        syncScaleUi();
      });

      const hardCommit = () => {
        if (setScaleDenom(scaleSlider.value)) {
          ui?.setDiameter?.(state.worldShortEdgeKm, false);
          syncScaleUi();
        }

        rect?.update(state.center, getRectDimensions(), true);
        writeOrderProps(true);
      };

      let isDragging = false;
      let debounceId = null;
      const debounceMs = 180;

      const onInput = () => {
        isDragging = true;
        clearTimeout(debounceId);
        softUpdate();
        debounceId = setTimeout(() => {
          isDragging = false;
          hardCommit();
        }, debounceMs);
      };

      const onChange = () => hardCommit();
      const onBlur = () => hardCommit();
      const onPointerUp = () => {
        if (!isDragging) return;
        isDragging = false;
        hardCommit();
      };

      scaleSlider.addEventListener('input', onInput, { passive: true });
      scaleSlider.addEventListener('change', onChange);
      scaleSlider.addEventListener('blur', onBlur);
      window.addEventListener('pointerup', onPointerUp, true);
      window.addEventListener('mouseup', onPointerUp, true);
      window.addEventListener('touchend', onPointerUp, { capture: true, passive: true });

      cleanupFns.push(() => {
        clearTimeout(debounceId);
        softUpdate.cancel?.();
        scaleSlider.removeEventListener('input', onInput);
        scaleSlider.removeEventListener('change', onChange);
        scaleSlider.removeEventListener('blur', onBlur);
        window.removeEventListener('pointerup', onPointerUp, true);
        window.removeEventListener('mouseup', onPointerUp, true);
        window.removeEventListener('touchend', onPointerUp, true);
      });
    }

    function wireRotationSlider() {
      if (rotationSliderWasWired || !rotationSlider) return;
      rotationSliderWasWired = true;

      rotationSlider.min = '0';
      rotationSlider.max = '180';
      rotationSlider.step = '1';
      rotationSlider.value = String(Math.round(state.rotationDeg));

      const softUpdate = U.rafThrottle(() => {
        if (!setRotationDeg(rotationSlider.value)) return;
        rect?.update(state.center, getRectDimensions(), false);
      });

      const hardCommit = () => {
        if (setRotationDeg(rotationSlider.value)) {
          rect?.update(state.center, getRectDimensions(), false);
        }
        writeOrderProps(true);
      };

      rotationSlider.addEventListener('input', softUpdate, { passive: true });
      rotationSlider.addEventListener('change', hardCommit);

      cleanupFns.push(() => {
        softUpdate.cancel?.();
        rotationSlider.removeEventListener('input', softUpdate);
        rotationSlider.removeEventListener('change', hardCommit);
      });
    }

    function handleStyleLoad() {
      const fitSelection = !hasLoadedStyle;
      hasLoadedStyle = true;
      isStyleTransitionPending = false;
      hideMapError(root);
      rebuildStyleDependentLayers(fitSelection);
    }

    function rebuildStyleDependentLayers(fit = false) {
      if (!map) return;

      if (!map.getSource('mapbox-dem')) {
        runOptionalEnhancement(() => {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14
          });
        });
      }

      if (map.getSource('mapbox-dem')) {
        runOptionalEnhancement(() => {
          map.setTerrain({
            source: 'mapbox-dem',
            exaggeration: 1
          });
        });
      }

      if (!map.getLayer(ids.sky)) {
        runOptionalEnhancement(() => {
          map.addLayer({
            id: ids.sky,
            type: 'sky',
            paint: {
              'sky-type': 'atmosphere',
              'sky-atmosphere-sun-intensity': 10
            }
          });
        });
      }

      removeSelectionLayers();

      if (typeof window.BM?.createRectangleLayer === 'function') {
        rect = window.BM.createRectangleLayer(map, ids, {
          lineColor: rectColor,
          fillOpacity: 0.12,
          beforeId: getTopLabelLayerId(),
          onUpdate: bm_peakExplorer_publishSelection
        });

        rect?.update?.(state.center, getRectDimensions(), fit);
      }

      syncReadOnlyUi();
      syncScaleUi();
      updateStyleSelectValue();
      writeOrderProps(true);
    }

    function removeSelectionLayers() {
      runOptionalEnhancement(() => {
        if (map?.getLayer(ids.fill)) map.removeLayer(ids.fill);
      });

      runOptionalEnhancement(() => {
        if (map?.getLayer(ids.line)) map.removeLayer(ids.line);
      });

      runOptionalEnhancement(() => {
        if (map?.getSource(ids.source)) map.removeSource(ids.source);
      });
    }

    function handleMapError() {
      if (!hasLoadedStyle || isStyleTransitionPending) {
        showMapError(root, ERROR_MESSAGES.mapLoad);
      }
    }

    function cleanup() {
      isDisposed = true;
      geolocationRequestId += 1;
      runCleanupFns(cleanupFns);
      cleanupFns.length = 0;

      if (focusResetTimeoutId) {
        clearTimeout(focusResetTimeoutId);
        focusResetTimeoutId = null;
      }

      if (interactionStatusTimeoutId) {
        window.clearTimeout(interactionStatusTimeoutId);
        interactionStatusTimeoutId = null;
      }

      if (mobileRevealFrameId) {
        window.cancelAnimationFrame(mobileRevealFrameId);
        mobileRevealFrameId = null;
      }

      runOptionalEnhancement(() => {
        map?.remove?.();
      });

      delete root.bm_peakExplorer_selection;

      root.__bmInited = false;
      root.__bmCleanup = null;
    }
  }

  function readSectionConfig(rootElement, sectionId, U) {
    const parsedConfig = parseSectionConfig(rootElement, sectionId);
    if (!parsedConfig) return null;

    if (!U) {
      return Object.assign({}, DEFAULT_CONFIG, parsedConfig);
    }

    return {
      token: typeof parsedConfig.token === 'string' ? parsedConfig.token.trim() : DEFAULT_CONFIG.token,
      initialCenter: [
        Number(parsedConfig.initialCenter?.[0]),
        Number(parsedConfig.initialCenter?.[1])
      ],
      printSizeCm: Number(parsedConfig.printSizeCm),
      printSizeLabel: typeof parsedConfig.printSizeLabel === 'string' && parsedConfig.printSizeLabel ? parsedConfig.printSizeLabel : DEFAULT_CONFIG.printSizeLabel,
      scaleMin: Number(parsedConfig.scaleMin),
      scaleMax: Number(parsedConfig.scaleMax),
      scaleStep: Number(parsedConfig.scaleStep),
      initialScale: Number(parsedConfig.initialScale),
      initialEdgeKm: Number(parsedConfig.initialEdgeKm),
      rotationDeg: Number(parsedConfig.rotationDeg),
      styleDefault: typeof parsedConfig.styleDefault === 'string' ? parsedConfig.styleDefault.trim() : DEFAULT_CONFIG.styleDefault,
      language: typeof parsedConfig.language === 'string' && parsedConfig.language ? parsedConfig.language : DEFAULT_CONFIG.language,
      countries: typeof parsedConfig.countries === 'string' && parsedConfig.countries ? parsedConfig.countries : DEFAULT_CONFIG.countries
    };
  }

  function parseSectionConfig(rootElement, sectionId) {
    try {
      const configId = `bm-config-${sectionId}`;
      const cfgEl = rootElement?.querySelector?.(`#${configId}`) || document.getElementById(configId);
      if (cfgEl?.textContent) {
        return JSON.parse(cfgEl.textContent);
      }
    } catch {
      return null;
    }

    return null;
  }

  function isValidConfig(cfg) {
    if (!cfg || !Array.isArray(cfg.initialCenter) || cfg.initialCenter.length < 2) return false;

    const lng = Number(cfg.initialCenter[0]);
    const lat = Number(cfg.initialCenter[1]);
    const printSizeCm = Number(cfg.printSizeCm);
    const scaleMin = Number(cfg.scaleMin);
    const scaleMax = Number(cfg.scaleMax);
    const scaleStep = Number(cfg.scaleStep);
    const initialScale = Number(cfg.initialScale);
    const rotationDeg = Number(cfg.rotationDeg);

    return Number.isFinite(lng) && lng >= -180 && lng <= 180
      && Number.isFinite(lat) && lat >= -85.051129 && lat <= 85.051129
      && Number.isFinite(printSizeCm) && printSizeCm > 0
      && Number.isFinite(scaleMin) && scaleMin > 0
      && Number.isFinite(scaleMax) && scaleMax >= scaleMin
      && Number.isFinite(scaleStep) && scaleStep > 0
      && Number.isFinite(initialScale) && initialScale >= scaleMin && initialScale <= scaleMax
      && Number.isFinite(rotationDeg) && rotationDeg >= 0 && rotationDeg <= 180;
  }

  function createState(cfg, U) {
    const sizeInfo = U.createSizeInfo([cfg.printSizeCm], { source: 'config', unit: 'cm' });
    const scaleMin = U.toNum(cfg.scaleMin, DEFAULT_CONFIG.scaleMin);
    const scaleMax = U.toNum(cfg.scaleMax, DEFAULT_CONFIG.scaleMax);
    const scaleStep = U.toNum(cfg.scaleStep, DEFAULT_CONFIG.scaleStep);
    const scaleDenom = U.toNum(cfg.initialScale, DEFAULT_CONFIG.initialScale);
    // Kompatibilität: Der Start-Maßstab bleibt wie bisher führend. Das ebenfalls
    // ausgelieferte initialEdgeKm wird nicht still gegen einen widersprüchlichen
    // Maßstab verrechnet; dafür wäre eine explizite Prioritätsentscheidung nötig.
    const worldShortEdgeKm = U.edgeKmFromScale(sizeInfo.printSizeCm, scaleDenom);
    const worldLongEdgeKm = worldShortEdgeKm * sizeInfo.aspectRatio;

    return {
      center: cfg.initialCenter.slice(),
      printSizeCm: sizeInfo.printSizeCm,
      printSizeLabel: cfg.printSizeLabel || sizeInfo.displayLabel,
      printShortEdgeCm: sizeInfo.shortestEdgeCm,
      printLongEdgeCm: sizeInfo.longestEdgeCm,
      printAspectRatio: sizeInfo.aspectRatio || 1,
      scaleMin,
      scaleMax,
      scaleStep,
      scaleDenom,
      worldShortEdgeKm,
      worldLongEdgeKm,
      worldWidthKm: worldLongEdgeKm,
      worldHeightKm: worldShortEdgeKm,
      rotationDeg: U.toNum(cfg.rotationDeg, DEFAULT_CONFIG.rotationDeg)
    };
  }

  function createLayerIds(sectionId) {
    return {
      source: `selection-src-${sectionId}`,
      fill: `selection-fill-${sectionId}`,
      line: `selection-line-${sectionId}`,
      sky: `sky-${sectionId}`
    };
  }

  function getAvailableStyles() {
    return Array.isArray(window.BM?.styles) && window.BM.styles.length
      ? window.BM.styles
      : DEFAULT_STYLES.slice();
  }

  function getRectColor(rootElement) {
    if (!rootElement) return '#253545';
    return getComputedStyle(rootElement).getPropertyValue('--bm-color').trim() || '#253545';
  }

  function showMapError(rootElement, message) {
    const errorEl = rootElement?.querySelector?.('[data-role="map-error"]');
    if (!errorEl) return;
    errorEl.textContent = message || ERROR_MESSAGES.mapLoad;
    errorEl.hidden = false;
  }

  function hideMapError(rootElement) {
    const errorEl = rootElement?.querySelector?.('[data-role="map-error"]');
    if (!errorEl) return;
    errorEl.hidden = true;
  }

  function extractStyleName(styleUri) {
    if (typeof styleUri !== 'string') return '';
    const parts = styleUri.split('/');
    return parts[parts.length - 1] || '';
  }

  function isValidPublicMapboxToken(token) {
    return typeof token === 'string' && token.startsWith('pk.');
  }

  function runOptionalEnhancement(operation) {
    try {
      operation();
      return true;
    } catch {
      // Terrain, Himmel und Cleanup sind optional; die Basiskarte bleibt funktionsfähig.
      return false;
    }
  }

  function applySharedState(state, sharedState, U) {
    if (!state || !sharedState || !U) return;

    if (
      Array.isArray(sharedState.center) &&
      Number.isFinite(Number(sharedState.center[0])) &&
      Number.isFinite(Number(sharedState.center[1]))
    ) {
      state.center = [Number(sharedState.center[0]), Number(sharedState.center[1])];
    }

    const rotationDeg = Number(sharedState.rotationDeg);
    if (Number.isFinite(rotationDeg) && rotationDeg >= 0 && rotationDeg <= 180) {
      state.rotationDeg = Math.round(rotationDeg);
    }

    const edgeKm = Number(sharedState.edgeKm);
    if (Number.isFinite(edgeKm) && edgeKm >= 1 && edgeKm <= 100) {
      state.worldShortEdgeKm = edgeKm;
      state.worldLongEdgeKm = edgeKm * state.printAspectRatio;
      state.worldWidthKm = state.worldLongEdgeKm;
      state.worldHeightKm = state.worldShortEdgeKm;
      state.scaleDenom = U.scaleFromEdge(edgeKm, state.printShortEdgeCm);
      return;
    }

    const scaleDenom = Number(sharedState.scaleDenom);
    if (
      Number.isFinite(scaleDenom) &&
      scaleDenom >= state.scaleMin &&
      scaleDenom <= state.scaleMax
    ) {
      state.scaleDenom = scaleDenom;
      state.worldShortEdgeKm = U.edgeKmFromScale(state.printShortEdgeCm, scaleDenom);
      state.worldLongEdgeKm = state.worldShortEdgeKm * state.printAspectRatio;
      state.worldWidthKm = state.worldLongEdgeKm;
      state.worldHeightKm = state.worldShortEdgeKm;
    }
  }

  function bm_peakExplorer_getSelection(sectionId = '') {
    const roots = Array.from(document.querySelectorAll('.bm-custom-peak'));
    const matchingRoot = sectionId
      ? roots
          .filter((root) => root.dataset.sectionId === sectionId && root.bm_peakExplorer_selection)
          .sort((a, b) => scoreRoot(b) - scoreRoot(a))[0]
      : roots
          .filter((root) => !root.hidden && root.getClientRects?.().length)
          .sort((a, b) => scoreRoot(b) - scoreRoot(a))[0] || roots[0];
    const selection = matchingRoot?.bm_peakExplorer_selection;
    if (!selection) return null;

    return {
      sectionId: selection.sectionId,
      center: selection.center.slice(),
      bounds: selection.bounds.slice(),
      widthKm: selection.widthKm,
      heightKm: selection.heightKm,
      printWidthCm: selection.printWidthCm,
      printHeightCm: selection.printHeightCm,
      rotationDeg: selection.rotationDeg,
      polygon: selection.polygon.map((coordinate) => coordinate.slice())
    };
  }

  function runCleanupFns(cleanupFns) {
    cleanupFns.slice().reverse().forEach(cleanup => {
      try {
        cleanup();
      } catch {
        // Ein fehlerhafter Fremd-Listener darf die übrige Bereinigung nicht verhindern.
      }
    });
  }

  function boot() {
    initPeakExplorerSections(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initPeakExplorerSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupPeakExplorerSections(event.target);
  });

  window.BM.initPeakExplorerSections = initPeakExplorerSections;
  window.BM.cleanupPeakExplorerSections = cleanupPeakExplorerSections;
  window.BM.bm_peakExplorer_getSelection = bm_peakExplorer_getSelection;
})();
